import { Injectable, signal, computed } from '@angular/core';

export enum VideoLayout {
  SINGLE = 1,
  SIDE_BY_SIDE = 2,
  LARGE_LEFT = 3,
  LARGE_RIGHT = 4,
  GRID_2X2 = 5,
  STACKED = 6,
}

export interface LayoutConfig {
  id: VideoLayout;
  label: string;
  playerCount: number;
}

const LAYOUT_CONFIGS: Record<VideoLayout, LayoutConfig> = {
  [VideoLayout.SINGLE]: { id: VideoLayout.SINGLE, label: 'Single View', playerCount: 1 },
  [VideoLayout.SIDE_BY_SIDE]: {
    id: VideoLayout.SIDE_BY_SIDE,
    label: 'Side by Side',
    playerCount: 2,
  },
  [VideoLayout.LARGE_LEFT]: { id: VideoLayout.LARGE_LEFT, label: 'Large Left', playerCount: 3 },
  [VideoLayout.LARGE_RIGHT]: { id: VideoLayout.LARGE_RIGHT, label: 'Large Right', playerCount: 3 },
  [VideoLayout.GRID_2X2]: { id: VideoLayout.GRID_2X2, label: '2×2 Grid', playerCount: 4 },
  [VideoLayout.STACKED]: { id: VideoLayout.STACKED, label: 'Stacked', playerCount: 2 },
};

@Injectable({ providedIn: 'root' })
export class VideoLayoutService {
  private readonly _activeLayout = signal<VideoLayout>(VideoLayout.SINGLE);

  readonly activeLayout = this._activeLayout.asReadonly();

  readonly layoutConfig = computed(() => LAYOUT_CONFIGS[this._activeLayout()]);

  readonly playerCount = computed(() => this.layoutConfig().playerCount);

  /** Number of placeholder slots needed (total - 1 for the main player) */
  readonly placeholderCount = computed(() => Math.max(0, this.playerCount() - 1));

  readonly placeholderSlots = computed(() =>
    Array.from({ length: this.placeholderCount() }, (_, i) => i + 1),
  );

  setLayout(layout: VideoLayout): void {
    this._activeLayout.set(layout);
  }

  isLayout(layout: VideoLayout): boolean {
    return this._activeLayout() === layout;
  }
}
